/**
 * PUBLIC multi-behavior token page (/t/:code). Standalone (no app shell). Phase 1.3b.
 *
 * Resolves a token's currently-available actions (resolve_token_public — no PII), then lets the holder
 * redeem each one through the public token-public edge fn. Identity-bound actions (requires_identity)
 * collect a 3-factor proof (email or member #, plus last name and zip) which the server verifies against
 * the token's subject; bearer actions (low-value, e.g. a drawing entry) redeem with just the code.
 *
 * F9 idempotency contract: ONE idempotency_key per redemption attempt, generated once per behavior and
 * REUSED across retries (so a retry after a transient 502 doesn't double-fulfil). Keys live in a ref so a
 * re-render never regenerates them.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import {
  resolveToken,
  redeemToken,
  type TokenBehavior,
  type TokenResolve,
  type TokenRedeemResult,
} from "../services/tokenPublic.service";
import {
  getTicketRewardStatus,
  type TicketRewardStatus,
} from "../services/redemption.service";

// Friendly call-to-action verbs per behavior type; fall back to the server-supplied label.
const VERB: Record<string, string> = {
  event_admission: "Check in",
  mint_reward_voucher: "Get my reward",
  drawing_entry: "Enter the drawing",
  discount_redeem: "Redeem discount",
};
function actionLabel(b: TokenBehavior): string {
  // resolve_token_public returns label = config.display_label, falling back to the raw `type`. When the
  // issuer set no custom display_label (so label === type, e.g. "event_admission"), show a friendly verb
  // instead of the internal type name; otherwise honor the custom label.
  if (b.label && b.label !== b.type) {
    return b.label;
  }
  return VERB[b.type] || "Redeem";
}

type IdentityMethod = "email" | "member";

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [otherCode, setOtherCode] = useState("");
  const goToCode = (e: React.FormEvent) => {
    e.preventDefault();
    let v = otherCode.trim();
    // Accept a pasted full URL or "/t/<code>" path, not just a bare code — extract the final token.
    const m = v.match(/\/t\/([^/?#]+)/i);
    if (m) {
      try {
        v = decodeURIComponent(m[1]);
      } catch {
        v = m[1];
      }
    }
    v = v.trim().toUpperCase();
    if (v) {
      navigate(`/t/${encodeURIComponent(v)}`);
      setOtherCode("");
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md">
        <img
          src="/USLSlogonobackground.png"
          alt="U.S. LawShield"
          className="mx-auto mb-6 h-12 w-auto"
        />
        {children}
        {/* Redeem a different code without hand-editing the URL (the code is otherwise URL-only). */}
        <form onSubmit={goToCode} className="mt-6 flex items-center gap-2">
          <input
            value={otherCode}
            onChange={(e) => setOtherCode(e.target.value)}
            placeholder="Have a different code? Enter it"
            aria-label="Enter a different code"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={!otherCode.trim()}
            className="rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            Go
          </button>
        </form>
      </div>
    </div>
  );
};

const TokenRedeem: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  // Always-current code, so an in-flight redemption can drop its writes if the user switches codes
  // (via the "different code" input) before it resolves — otherwise stale result/reward would land on
  // the new code's view.
  const codeRef = useRef(code);
  codeRef.current = code;

  const [loading, setLoading] = useState(true);
  const [resolved, setResolved] = useState<TokenResolve | null>(null);

  // The behavior currently being acted on (its identity form / button is active).
  const [activeId, setActiveId] = useState<string | null>(null);
  const [method, setMethod] = useState<IdentityMethod>("email");
  const [email, setEmail] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [lastName, setLastName] = useState("");
  const [zip, setZip] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<TokenRedeemResult | null>(null);
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  // Event-Ticket reward, surfaced inline on /t/ and gated on check-in (status='not_checked_in' until then).
  const [reward, setReward] = useState<TicketRewardStatus | null>(null);
  // The code the current `reward` was fetched for. The redirect gates on this so a stale reward from a
  // previous code can never combine with a newly-changed URL code (audit HIGH).
  const [rewardCode, setRewardCode] = useState<string | undefined>(undefined);

  // F9: stable idempotency key per behavior attempt, reused on retry.
  const idemKeys = useRef<Record<string, string>>({});
  const getIdemKey = (behaviorId: string): string => {
    if (!idemKeys.current[behaviorId]) {
      idemKeys.current[behaviorId] =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${behaviorId}-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
    }
    return idemKeys.current[behaviorId];
  };

  const load = useCallback(async () => {
    if (!code) {
      return;
    }
    const startCode = code;
    setLoading(true);
    try {
      const [r, rw] = await Promise.all([
        resolveToken(code),
        getTicketRewardStatus(code).catch(() => null),
      ]);
      // Drop stale data if the holder switched codes while this load was in flight — otherwise the old
      // code's resolve/reward would overwrite the new code's view.
      if (codeRef.current !== startCode) {
        return;
      }
      setResolved(r);
      setReward(rw);
      setRewardCode(startCode);
    } catch {
      if (codeRef.current === startCode) {
        setResolved({ found: false, available_behaviors: [] });
      }
    } finally {
      if (codeRef.current === startCode) {
        setLoading(false);
      }
    }
  }, [code]);

  useEffect(() => {
    void load();
  }, [load]);

  // When the URL code changes (e.g. via "enter a different code"), clear the previous token's transient
  // state so the new code starts fresh — the component stays mounted across the param change, so without
  // this a prior check-in's "done"/result state would leak into the next code. (load() refreshes
  // resolved/reward.) setState setters are stable, so [code] is the only dependency.
  useEffect(() => {
    setDoneIds(new Set());
    setResult(null);
    setActiveId(null);
    setEmail("");
    setMemberNumber("");
    setLastName("");
    setZip("");
    setMethod("email");
    // Clear the prior code's reward so the redirect can't fire with stale status for the new code.
    setReward(null);
    setRewardCode(undefined);
    idemKeys.current = {};
  }, [code]);

  // Ticket upgraded to a redeemable voucher → send the holder straight to the redemption page the
  // booth uses, instead of the value/code panel (EIB tester 2026-08-14). Only `status==='redeemable'`
  // (checked in + reward assigned + within the window, unredeemed) qualifies as "upgraded to a
  // voucher"; a pending/no-reward ticket keeps the current panel. `rewardCode === code` binds the
  // status to the code it was fetched for — a stale reward from a previous code cannot redirect the
  // new one (audit HIGH). `replace` avoids a Back-loop.
  useEffect(() => {
    if (
      code &&
      rewardCode === code &&
      reward?.found &&
      reward.status === "redeemable"
    ) {
      navigate(`/redeem?code=${encodeURIComponent(code.toUpperCase())}`, {
        replace: true,
      });
    }
  }, [code, rewardCode, reward?.found, reward?.status, navigate]);

  const behaviors = resolved?.available_behaviors ?? [];
  // event_admission is read-only here (check-in is tablet-only); "self-serve" = any other behavior.
  const hasSelfServe = behaviors.some((b) => b.type !== "event_admission");
  const active = behaviors.find((b) => b.behavior_id === activeId) || null;
  const rewardUnlockTime =
    typeof reward?.window_start_ms === "number"
      ? new Date(reward.window_start_ms).toLocaleTimeString("en-US", {
          timeZone: "America/Chicago",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  function resetIdentity() {
    setEmail("");
    setMemberNumber("");
    setLastName("");
    setZip("");
    setMethod("email");
  }

  async function doRedeem(b: TokenBehavior) {
    if (!code || submitting) {
      return;
    }
    const startCode = code;
    setSubmitting(true);
    setResult(null);
    try {
      const identity = b.requires_identity
        ? {
            email: method === "email" ? email.trim() : undefined,
            memberNumber: method === "member" ? memberNumber.trim() : undefined,
            lastName: lastName.trim(),
            zip: zip.trim(),
          }
        : undefined;
      const res = await redeemToken({
        code,
        behaviorId: b.behavior_id,
        idempotencyKey: getIdemKey(b.behavior_id),
        identity,
      });
      // Dropped if the holder switched codes mid-request — don't apply this code's result to another.
      if (codeRef.current !== startCode) {
        return;
      }
      setResult(res);
      if (res.status === 200 && res.redeemed) {
        setDoneIds((prev) => new Set(prev).add(b.behavior_id));
        // The action is consumed — a successful attempt's key never needs reuse.
        delete idemKeys.current[b.behavior_id];
        setActiveId(null);
        resetIdentity();
        // Self-contained: the reward unlocks the instant check-in completes — refresh it inline
        // (server re-verifies actual check-in via ticket_is_checked_in) instead of routing to /redeem.
        try {
          const rw = await getTicketRewardStatus(startCode);
          if (codeRef.current === startCode) {
            setReward(rw);
            setRewardCode(startCode);
          }
        } catch {
          /* keep prior reward state */
        }
      }
    } catch {
      if (codeRef.current === startCode) {
        setResult({
          status: 0,
          error: "Network error. Please check your connection and try again.",
        });
      }
    } finally {
      // Only clear OUR submit state — a stale redeem for a switched-away code must not un-gate the
      // new code's in-flight submission.
      if (codeRef.current === startCode) {
        setSubmitting(false);
      }
    }
  }

  const identityIncomplete =
    (method === "email" ? !email.trim() : !memberNumber.trim()) ||
    !lastName.trim() ||
    !zip.trim();

  // ---- Render states ----------------------------------------------------------------------------

  if (loading) {
    return (
      <Shell>
        <div className="text-center text-gray-500 py-20">Loading…</div>
      </Shell>
    );
  }

  if (!resolved?.found) {
    return (
      <Shell>
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            Code not found
          </h1>
          <p className="text-sm text-gray-600">
            We couldn&apos;t find anything for this code. Please double-check
            it, or see a USLS representative.
          </p>
        </div>
      </Shell>
    );
  }

  if (behaviors.length === 0) {
    return (
      <Shell>
        <div className="bg-white rounded-xl shadow p-6 sm:p-8">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">
              {reward?.found ? "You're all set" : "Nothing left to redeem"}
            </h1>
            {!reward?.found && (
              <p className="text-sm text-gray-600">
                This code has no available actions right now — it may already
                have been used.
              </p>
            )}
          </div>
          {reward?.found && (
            <RewardPanel
              reward={reward}
              code={code}
              unlockTime={rewardUnlockTime}
            />
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="bg-white rounded-xl shadow p-6 sm:p-8">
        <h1 className="text-lg font-semibold text-gray-900 mb-1">
          {hasSelfServe ? "Redeem your code" : "Your ticket"}
        </h1>
        <p className="text-sm text-gray-600 mb-5">
          {hasSelfServe
            ? "Choose an action below."
            : "Present this at the entrance to check in. Any reward unlocks after you're checked in."}
        </p>

        <div className="space-y-3">
          {behaviors.map((b) => {
            // An event_admission action is redundant once the holder is already checked in (e.g. a
            // self-checkin walk-in marks attendance without redeeming this behavior) — show it as done.
            const done =
              doneIds.has(b.behavior_id) ||
              (b.type === "event_admission" && reward?.checked_in === true);
            const isActive = activeId === b.behavior_id;
            // Presence-gated check-in: this personal page NEVER checks anyone in. An event_admission
            // behavior is shown read-only ("present at the entrance" / "checked in") — actual check-in
            // happens only at the venue tablet (/self-checkin). The server also refuses event_admission
            // from the public token path.
            const isAdmission = b.type === "event_admission";
            return (
              <div
                key={b.behavior_id}
                className="rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {isAdmission ? "Event ticket" : actionLabel(b)}
                    </p>
                    {isAdmission ? (
                      <p className="text-xs text-gray-500">
                        {done
                          ? "You're checked in."
                          : "Present this at the entrance to check in."}
                      </p>
                    ) : (
                      b.requires_identity && (
                        <p className="text-xs text-gray-500">
                          Requires identity verification
                        </p>
                      )
                    )}
                  </div>
                  {done ? (
                    <span className="shrink-0 text-sm font-medium text-green-700">
                      {isAdmission ? "Checked in ✓" : "Done ✓"}
                    </span>
                  ) : isAdmission ? (
                    <span className="shrink-0 text-sm font-medium text-blue-700">
                      Valid 🎟️
                    </span>
                  ) : b.requires_identity ? (
                    <button
                      type="button"
                      onClick={() => {
                        setResult(null);
                        setActiveId(isActive ? null : b.behavior_id);
                      }}
                      className="shrink-0 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      {isActive ? "Cancel" : actionLabel(b)}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => void doRedeem(b)}
                      className="shrink-0 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {submitting ? "…" : actionLabel(b)}
                    </button>
                  )}
                </div>

                {/* Reveal for the active identity-bound behavior: the 3-factor identity form. */}
                {isActive && !isAdmission && b.requires_identity && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    <div className="flex rounded-md bg-gray-100 p-1 text-sm">
                      <button
                        type="button"
                        onClick={() => setMethod("email")}
                        className={`flex-1 rounded py-1.5 font-medium ${
                          method === "email"
                            ? "bg-white shadow text-gray-900"
                            : "text-gray-500"
                        }`}
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setMethod("member")}
                        className={`flex-1 rounded py-1.5 font-medium ${
                          method === "member"
                            ? "bg-white shadow text-gray-900"
                            : "text-gray-500"
                        }`}
                      >
                        Member #
                      </button>
                    </div>

                    {method === "email" ? (
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="Email on your membership"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder="Member number"
                        value={memberNumber}
                        onChange={(e) => setMemberNumber(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      />
                    )}
                    <input
                      type="text"
                      autoComplete="family-name"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      placeholder="ZIP code"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      disabled={submitting || identityIncomplete}
                      onClick={() => void doRedeem(b)}
                      className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {submitting
                        ? "Verifying…"
                        : "Verify & " + actionLabel(b).toLowerCase()}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Event-Ticket reward — shown inline and gated on completed check-in. When redeemable its QR
            now opens /redeem for the booth to SCAN (#3, 2026-08-12) — no self-serve redeem control on
            the holder's own screen; the server re-verifies check-in via ticket_is_checked_in. */}
        {reward?.found && (
          <RewardPanel
            reward={reward}
            code={code}
            unlockTime={rewardUnlockTime}
          />
        )}

        {/* Result / error feedback */}
        {result && (
          <ResultNote
            result={result}
            onRetry={active ? () => void doRedeem(active) : undefined}
          />
        )}
      </div>
    </Shell>
  );
};

/** The ticket's reward, gated on check-in: locked hint until checked in, then value + code inline. */
const RewardPanel: React.FC<{
  reward: TicketRewardStatus;
  code: string | undefined;
  unlockTime: string | null;
}> = ({ reward, code, unlockTime }) => {
  const shown = code ? code.toUpperCase() : "";
  // Members can't self-redeem — the (offer-configurable) message directs them to redeem at the event.
  const msg = reward.redemption_message?.trim() || "Redeem it at the event.";

  // #3 (2026-08-12): route the booth straight to the redemption page. When the VENDOR scans this
  // reward's QR (with their device / the /redeem scanner) it opens /redeem?code=<TK->, which prefills
  // + auto-validates the ticket reward (VoucherRedemption enforces the check-in gate server-side), so
  // the vendor no longer keys the code in by hand. Previously the QR encoded the bare code, forcing a
  // manual re-entry. Deliberately NO tappable link here — that would let the holder self-redeem their
  // own reward one-tap on their own phone before reaching the booth.
  const redeemUrl = shown
    ? `${window.location.origin}/redeem?code=${encodeURIComponent(shown)}`
    : "";

  // Render a scannable QR that opens the redemption page so the booth can scan it instead of keying it in.
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (reward.status === "redeemable" && redeemUrl) {
      QRCode.toDataURL(redeemUrl, { width: 240, margin: 1 })
        .then((url) => alive && setQrDataUrl(url))
        .catch(() => alive && setQrDataUrl(null));
    } else {
      setQrDataUrl(null);
    }
    return () => {
      alive = false;
    };
  }, [reward.status, redeemUrl]);

  if (reward.status === "not_checked_in") {
    return (
      <div className="mt-5 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-center text-sm text-gray-600">
        🎁 A reward unlocks after you check in.
      </div>
    );
  }
  if (reward.status === "redeemable") {
    return (
      <div className="mt-5 rounded-lg border border-green-200 bg-green-50 px-4 py-4 text-center">
        <p className="text-xs uppercase tracking-wide text-green-700">
          Your reward
        </p>
        {/* Only show a dollar amount for a monetary reward. A non-monetary reward (e.g. a drawing
            entry / physical gift) carries $0 value — the code + QR + message are the reward. */}
        {Number(reward.redeemable_value ?? 0) > 0 && (
          <p className="text-3xl font-bold text-gray-900">
            ${Number(reward.redeemable_value ?? 0).toFixed(0)}
          </p>
        )}
        <p className="mt-1 text-lg font-bold tracking-wider text-gray-900">
          {shown}
        </p>
        {qrDataUrl && (
          <img
            src={qrDataUrl}
            alt={`QR code to redeem reward ${shown}`}
            className="mx-auto mt-3 h-44 w-44 rounded bg-white p-1"
          />
        )}
        <p className="mt-2 text-sm text-gray-600">{msg}</p>
      </div>
    );
  }
  if (reward.status === "not_yet") {
    return (
      <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm text-blue-800">
        You&apos;re checked in — your reward unlocks
        {unlockTime ? ` at ${unlockTime}` : " soon"}. Check back then.
      </div>
    );
  }
  if (reward.status === "pending") {
    return (
      <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm text-blue-800">
        You&apos;re checked in — your reward value is being set. Check back
        shortly.
      </div>
    );
  }
  if (reward.status === "already_redeemed") {
    return (
      <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm text-gray-600">
        Your reward has already been redeemed.
      </div>
    );
  }
  return null;
};

const ResultNote: React.FC<{
  result: TokenRedeemResult;
  onRetry?: () => void;
}> = ({ result, onRetry }) => {
  if (result.status === 200 && result.redeemed) {
    return (
      <div className="mt-4 rounded-md bg-green-50 px-4 py-3 text-sm text-green-800">
        <p>
          {result.fulfilled
            ? "All set — you're good to go!"
            : "Recorded. You're all set."}
        </p>
        {result.rewardVoucherCode && (
          <div className="mt-3 rounded-md border border-green-200 bg-white px-3 py-2 text-center">
            <p className="text-xs text-gray-500">Your reward code</p>
            <p className="text-lg font-bold tracking-wider text-gray-900">
              {result.rewardVoucherCode}
            </p>
            <a
              href={`/redeem?code=${encodeURIComponent(result.rewardVoucherCode)}`}
              className="mt-1 inline-block text-xs font-medium text-blue-700 underline"
            >
              View &amp; redeem
            </a>
          </div>
        )}
      </div>
    );
  }
  // 502 transient — let the holder retry with the SAME idempotency key (no double-fulfilment).
  if (result.status === 502 && onRetry) {
    return (
      <div className="mt-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <p>{result.error || "Something went wrong. Please try again."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 font-medium text-amber-900 underline"
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <p className="mt-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
      {result.error ||
        "This code can't be redeemed right now. Please see a USLS representative."}
    </p>
  );
};

export default TokenRedeem;
